import React from "react";
import { Typography, Box, TextField, Checkbox, Radio, Slider } from "@material-ui/core";
import { evaluate } from "feelin";
import LinkifiedText from "./LinkifiedText";
import { previewStyles as styles } from "./previewStyles";
import type { ResolvedResponseType, ResolvedQuestion, ResolvedQuestionRef, Questionnaire } from "../../types/resolved";
import type { Visibility, Requirement } from "../../types/common";
import { STAR_COUNT } from "../../types/common";

type VariantItem = { id: number; text: string; parent_label?: string | null };
type GroupedVariants = { label: string | null; variants: VariantItem[] }[];

const groupVariantsByParentLabel = (variants: VariantItem[]): GroupedVariants => {
  const groups: GroupedVariants = [];
  for (const v of variants) {
    const label = v.parent_label ?? null;
    const last = groups[groups.length - 1];
    if (last && last.label === label) {
      last.variants.push(v);
    } else {
      groups.push({ label, variants: [v] });
    }
  }
  return groups;
};

export const AutoFillContext = React.createContext(0);

export type FormEntry = {
  questionId: number;
  value: unknown;
  feelId: string;
  feelValue: unknown;
};
export type FormValues = Record<number, FormEntry>;
export const FormValuesContext = React.createContext<{
  register: (questionId: number, entry: FormEntry) => void;
  unregister: (questionId: number) => void;
}>({ register: () => {}, unregister: () => {} });

export const PageContext = React.createContext<number | null>(null);

export const ValidationContext = React.createContext<{
  registerRequired: (questionId: number, pageIndex: number | null) => void;
  unregisterRequired: (questionId: number) => void;
  errorIds: Set<number>;
  validate: (pageIndex?: number | null) => boolean;
}>({ registerRequired: () => {}, unregisterRequired: () => {}, errorIds: new Set(), validate: () => true });

export const FeelContext = React.createContext<Record<string, unknown>>({});

const getOptionFeelIds = (responseType: ResolvedResponseType): string[] => {
  switch (responseType._type) {
    case "select":
    case "select-or-other":
    case "multi-select":
    case "multi-select-or-other":
      return responseType.variants.map(v => v.feel_id).filter(Boolean);
    case "multi-select-grouped":
      return responseType.groups.flatMap(g => g.variants.map(v => v.feel_id)).filter(Boolean);
    default:
      return [];
  }
};

export const collectFeelSeed = (questions: ReadonlyArray<ResolvedQuestion>): Record<string, unknown> => {
  const seed: Record<string, unknown> = {};
  for (const q of questions) {
    if (q.response_type) for (const optId of getOptionFeelIds(q.response_type)) seed[optId] = optId;
  }
  return seed;
};

export const collectQuestionnaireFeelSeed = (questionnaire: Questionnaire): Record<string, unknown> => {
  const questions: ResolvedQuestion[] = [];
  for (const page of questionnaire.pages) {
    for (const part of page.parts) {
      if (part._type === "question") questions.push(part.question);
      else for (const ref of part.question_group.questions) questions.push(ref.question);
    }
  }
  return collectFeelSeed(questions);
};

const toFeelValue = (
  responseType: ResolvedResponseType,
  state: {
    selectedValue: number | null;
    checkedValues: Set<string>;
    checkboxValue: boolean | null;
    sliderValue: number | null;
    textValue: string;
    integerValue: string;
    dateValue: string;
    otherSelected: boolean;
  }
): unknown => {
  switch (responseType._type) {
    case "select":
    case "select-or-other": {
      if (state.otherSelected) return null;
      const variant = responseType.variants.find(v => v.id === state.selectedValue);
      return variant?.feel_id ?? null;
    }
    case "multi-select":
    case "multi-select-or-other":
      return responseType.variants.filter(v => state.checkedValues.has(String(v.id))).map(v => v.feel_id);
    case "multi-select-grouped":
      return responseType.groups
        .flatMap(g => g.variants)
        .filter(v => state.checkedValues.has(String(v.id)))
        .map(v => v.feel_id);
    case "checkbox":
      return state.checkboxValue;
    case "vas":
    case "number-range":
    case "stars":
      return state.sliderValue;
    case "integer":
      return state.integerValue ? Number(state.integerValue) : null;
    case "date":
      return state.dateValue || null;
    case "text":
    case "text-area":
      return state.textValue || null;
    default:
      return null;
  }
};

export const RespondedContext = React.createContext<Set<number>>(new Set());

export const evaluateFeelPredicate = (predicate: string, context: Record<string, unknown>): boolean => {
  try {
    const { value } = evaluate(predicate, context);
    return value === true;
  } catch (err) {
    console.warn("FEEL predicate evaluation failed", { predicate, error: err });
    return false;
  }
};

export const computeRevealedQuestions = (
  questionRefs: ReadonlyArray<{ questionId: number; visibility: Visibility; requirement?: Requirement }>,
  feelCtx: Record<string, unknown>,
  respondedIds: Set<number>
): Set<number> => {
  const { revealed } = questionRefs.reduce<{ revealed: Set<number>; blocked: boolean }>(
    (acc, qRef) => {
      const isVisible = qRef.visibility === "visible" || evaluateFeelPredicate(qRef.visibility.visibleWhen, feelCtx);
      if (!isVisible || acc.blocked) return acc;
      acc.revealed.add(qRef.questionId);
      const isRequired =
        qRef.requirement === "required" ||
        (typeof qRef.requirement === "object" && evaluateFeelPredicate(qRef.requirement.requiredWhen, feelCtx));
      return { revealed: acc.revealed, blocked: isRequired && !respondedIds.has(qRef.questionId) };
    },
    { revealed: new Set<number>(), blocked: false }
  );
  return revealed;
};

export const ConditionalPart: React.VFC<{
  visibility: Visibility;
  requirement?: Requirement;
  children: (isRequired: boolean) => React.ReactNode;
}> = ({ visibility, requirement, children }) => {
  const feelContext = React.useContext(FeelContext);

  const isVisible = visibility === "visible" || evaluateFeelPredicate(visibility.visibleWhen, feelContext);

  const isRequired =
    requirement === "required" ||
    (typeof requirement === "object" && evaluateFeelPredicate(requirement.requiredWhen, feelContext));

  if (!isVisible) return null;
  return <>{children(isRequired)}</>;
};

const generateAutoFillValue = (responseType: ResolvedResponseType) => {
  switch (responseType._type) {
    case "select":
    case "select-or-other":
      return { selectedValue: responseType.variants[0]?.id ?? null };
    case "multi-select":
      return { checkedValues: new Set(responseType.variants.slice(0, 2).map(v => String(v.id))) };
    case "multi-select-or-other":
      return { checkedValues: new Set(responseType.variants.slice(0, 2).map(v => String(v.id))) };
    case "multi-select-grouped": {
      const values = responseType.groups.flatMap(g => g.variants.slice(0, 1).map(v => String(v.id)));
      return { checkedValues: new Set(values.slice(0, 2)) };
    }
    case "checkbox":
      return { checkboxValue: true };
    case "vas":
      return { sliderValue: Math.floor((responseType.min + responseType.max) / 2) };
    case "number-range":
      return { sliderValue: Math.floor((responseType.min.value + responseType.max.value) / 2) };
    case "stars":
      return { sliderValue: STAR_COUNT };
    case "integer":
      return { integerValue: String(Math.floor((responseType.min + responseType.max) / 2)) };
    case "date":
      return { dateValue: new Date().toISOString().split("T")[0] };
    case "text":
    case "text-area":
      return { textValue: "Sample text" };
    default:
      return {};
  }
};

export const ResolvedResponseTypePreview: React.VFC<{
  responseType: ResolvedResponseType;
  questionId?: number;
  feelId?: string;
}> = ({ responseType, questionId, feelId }) => {
  const autoFillTrigger = React.useContext(AutoFillContext);
  const formValues = React.useContext(FormValuesContext);
  const [selectedValue, setSelectedValue] = React.useState<number | null>(null);
  const [checkedValues, setCheckedValues] = React.useState<Set<string>>(new Set());
  const [checkboxValue, setCheckboxValue] = React.useState<boolean | null>(null);
  const [sliderValue, setSliderValue] = React.useState<number | null>(null);
  const [textValue, setTextValue] = React.useState("");
  const [integerValue, setIntegerValue] = React.useState("");
  const [dateValue, setDateValue] = React.useState("");
  const [otherSelected, setOtherSelected] = React.useState(false);
  const [otherText, setOtherText] = React.useState("");
  const [expandedGroups, setExpandedGroups] = React.useState<Set<string>>(new Set());

  React.useEffect(() => {
    if (questionId == null) return;
    return () => formValues.unregister(questionId);
  }, [questionId, formValues]);

  React.useEffect(() => {
    if (questionId == null) return;
    const getResponse = (): unknown => {
      switch (responseType._type) {
        case "select":
          return selectedValue != null ? { optionId: selectedValue } : null;
        case "select-or-other":
          return otherSelected && otherText.trim()
            ? { other: otherText }
            : selectedValue != null
              ? { optionId: selectedValue }
              : null;
        case "multi-select":
          return responseType.variants.filter(v => checkedValues.has(String(v.id))).map(v => ({ optionId: v.id }));
        case "multi-select-or-other": {
          const selected = responseType.variants
            .filter(v => checkedValues.has(String(v.id)))
            .map(v => ({ optionId: v.id }));
          return otherText.trim() ? [...selected, { other: otherText }] : selected;
        }
        case "multi-select-grouped":
          return responseType.groups.flatMap(g =>
            g.variants.filter(v => checkedValues.has(String(v.id))).map(v => ({ optionId: v.id }))
          );
        case "checkbox":
          return checkboxValue;
        case "vas":
        case "number-range":
        case "stars":
          return sliderValue;
        case "integer":
          return integerValue ? Number(integerValue) : null;
        case "date":
          return dateValue || null;
        case "text":
        case "text-area":
          return textValue || null;
        default:
          return null;
      }
    };
    const fv = toFeelValue(responseType, {
      selectedValue,
      checkedValues,
      checkboxValue,
      sliderValue,
      textValue,
      integerValue,
      dateValue,
      otherSelected,
    });
    formValues.register(questionId, {
      questionId,
      value: getResponse(),
      feelId: feelId ?? "",
      feelValue: fv,
    });
  }, [
    questionId,
    selectedValue,
    checkedValues,
    checkboxValue,
    sliderValue,
    textValue,
    integerValue,
    dateValue,
    otherSelected,
    otherText,
    formValues,
    responseType,
    feelId,
  ]);

  React.useEffect(() => {
    if (autoFillTrigger === 0) return;
    const v = generateAutoFillValue(responseType);
    if ("selectedValue" in v) setSelectedValue(v.selectedValue ?? null);
    if ("checkedValues" in v) setCheckedValues(v.checkedValues ?? new Set());
    if ("checkboxValue" in v) setCheckboxValue(v.checkboxValue ?? null);
    if ("sliderValue" in v) setSliderValue(v.sliderValue ?? null);
    if ("integerValue" in v) setIntegerValue(v.integerValue ?? "");
    if ("dateValue" in v) setDateValue(v.dateValue ?? "");
    if ("textValue" in v) setTextValue(v.textValue ?? "");
  }, [autoFillTrigger, responseType]);

  switch (responseType._type) {
    case "select":
    case "select-or-other": {
      const groups = groupVariantsByParentLabel(responseType.variants);
      return (
        <div className={styles.selectContainer}>
          {groups.map((group, gi) => (
            <React.Fragment key={gi}>
              {group.label && (
                <Typography variant="subtitle2" className={styles.groupHeader}>
                  {group.label}
                </Typography>
              )}
              {group.label ? (
                <div className={styles.subVariantsContainer}>
                  {group.variants.map((v, i) => (
                    <label
                      key={v.id ?? i}
                      className={styles.radioOption}
                      onClick={() => {
                        setSelectedValue(v.id);
                        setOtherSelected(false);
                      }}
                    >
                      <Radio
                        checked={selectedValue === v.id && !otherSelected}
                        onChange={() => {
                          setSelectedValue(v.id);
                          setOtherSelected(false);
                        }}
                        color="primary"
                        className={styles.radioButton}
                      />
                      <span className={styles.radioLabel}>
                        <LinkifiedText text={v.text} />
                      </span>
                    </label>
                  ))}
                </div>
              ) : (
                group.variants.map((v, i) => (
                  <label
                    key={v.id ?? i}
                    className={styles.radioOption}
                    onClick={() => {
                      setSelectedValue(v.id);
                      setOtherSelected(false);
                    }}
                  >
                    <Radio
                      checked={selectedValue === v.id && !otherSelected}
                      onChange={() => {
                        setSelectedValue(v.id);
                        setOtherSelected(false);
                      }}
                      color="primary"
                      className={styles.radioButton}
                    />
                    <span className={styles.radioLabel}>
                      <LinkifiedText text={v.text} />
                    </span>
                  </label>
                ))
              )}
            </React.Fragment>
          ))}
          {responseType._type === "select-or-other" && (
            <>
              <label className={styles.radioOption} onClick={() => setOtherSelected(true)}>
                <Radio
                  checked={otherSelected}
                  onChange={() => setOtherSelected(true)}
                  color="primary"
                  className={styles.radioButton}
                />
                <span className={styles.radioLabel}>Other (please specify)</span>
              </label>
              {otherSelected && (
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  placeholder="Please specify"
                  value={otherText}
                  onChange={e => setOtherText(e.target.value)}
                  style={{ marginTop: 4 }}
                />
              )}
            </>
          )}
        </div>
      );
    }
    case "multi-select":
    case "multi-select-or-other": {
      const groups = groupVariantsByParentLabel(responseType.variants);
      const toggleGroup = (label: string) => {
        const collapsing = expandedGroups.has(label);
        setExpandedGroups(prev => {
          const s = new Set(prev);
          collapsing ? s.delete(label) : s.add(label);
          return s;
        });
        if (collapsing) {
          const idsToRemove = groups.find(g => g.label === label)?.variants.map(v => String(v.id)) ?? [];
          setCheckedValues(prev => {
            const s = new Set(prev);
            idsToRemove.forEach(id => s.delete(id));
            return s;
          });
        }
      };
      return (
        <div className={styles.multiSelectContainer}>
          {groups.map((group, gi) => (
            <React.Fragment key={gi}>
              {group.label ? (
                <>
                  <div className={styles.checkboxOption} onClick={() => toggleGroup(group.label!)}>
                    <Checkbox
                      checked={expandedGroups.has(group.label!)}
                      color="primary"
                      className={styles.checkboxButton}
                      onClick={e => {
                        e.stopPropagation();
                        toggleGroup(group.label!);
                      }}
                    />
                    <span className={styles.checkboxLabel}>{group.label}</span>
                  </div>
                  {expandedGroups.has(group.label) && (
                    <div className={styles.subVariantsContainer}>
                      {group.variants.map(v => (
                        <label key={v.id} className={styles.checkboxOption}>
                          <Checkbox
                            checked={checkedValues.has(String(v.id))}
                            onChange={e => {
                              const s = new Set(checkedValues);
                              e.target.checked ? s.add(String(v.id)) : s.delete(String(v.id));
                              setCheckedValues(s);
                            }}
                            color="primary"
                            size="small"
                            className={styles.checkboxButton}
                          />
                          <span className={styles.checkboxLabel}>
                            <LinkifiedText text={v.text} />
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                group.variants.map(v => (
                  <label key={v.id} className={styles.checkboxOption}>
                    <Checkbox
                      checked={checkedValues.has(String(v.id))}
                      onChange={e => {
                        const s = new Set(checkedValues);
                        e.target.checked ? s.add(String(v.id)) : s.delete(String(v.id));
                        setCheckedValues(s);
                      }}
                      color="primary"
                      className={styles.checkboxButton}
                    />
                    <span className={styles.checkboxLabel}>
                      <LinkifiedText text={v.text} />
                    </span>
                  </label>
                ))
              )}
            </React.Fragment>
          ))}
          {responseType._type === "multi-select-or-other" && (
            <>
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                Other (please specify)
              </Typography>
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                placeholder="Please specify"
                value={otherText}
                onChange={e => setOtherText(e.target.value)}
              />
            </>
          )}
        </div>
      );
    }
    case "multi-select-grouped": {
      const groups = responseType.groups;
      return (
        <div className={styles.multiSelectContainer}>
          {groups.map((group, gi) => (
            <div key={gi} className={styles.groupSection}>
              <Typography variant="subtitle2" className={styles.groupHeader}>
                {group.label}
              </Typography>
              {group.variants.map(v => (
                <label key={v.id} className={styles.checkboxOption}>
                  <Checkbox
                    checked={checkedValues.has(String(v.id))}
                    onChange={e => {
                      const s = new Set(checkedValues);
                      e.target.checked ? s.add(String(v.id)) : s.delete(String(v.id));
                      setCheckedValues(s);
                    }}
                    color="primary"
                    className={styles.checkboxButton}
                  />
                  <span className={styles.checkboxLabel}>
                    <LinkifiedText text={v.text} />
                  </span>
                </label>
              ))}
            </div>
          ))}
        </div>
      );
    }
    case "checkbox":
      return (
        <div className={styles.booleanContainer}>
          <label className={styles.booleanOption} onClick={() => setCheckboxValue(true)}>
            <Radio
              checked={checkboxValue === true}
              onChange={() => setCheckboxValue(true)}
              color="primary"
              className={styles.radioButton}
            />
            <span className={styles.radioLabel}>Yes</span>
          </label>
          <label className={styles.booleanOption} onClick={() => setCheckboxValue(false)}>
            <Radio
              checked={checkboxValue === false}
              onChange={() => setCheckboxValue(false)}
              color="primary"
              className={styles.radioButton}
            />
            <span className={styles.radioLabel}>No</span>
          </label>
        </div>
      );
    case "vas": {
      const { min, max } = responseType;
      return (
        <div className={styles.vasContainer} style={{ opacity: sliderValue === null ? 0.4 : 1 }}>
          <div className={styles.vasLabels}>
            <span>{min}</span>
            <span>{max}</span>
          </div>
          <Slider
            value={sliderValue ?? Math.floor((min + max) / 2)}
            min={min}
            max={max}
            onChange={(_, v) => setSliderValue(v as number)}
            valueLabelDisplay={sliderValue !== null ? "on" : "auto"}
          />
        </div>
      );
    }
    case "number-range": {
      const { min: minObj, max: maxObj } = responseType;
      const minLabel = minObj.label ?? "";
      const maxLabel = maxObj.label ?? "";
      const colorMap: Record<string, string> = { positive: "#4caf50", negative: "#f44336", neutral: "#2196f3" };
      const minColor = minObj.color ?? "negative";
      const maxColor = maxObj.color ?? "positive";
      const count = maxObj.value - minObj.value + 1;
      const boxes = Array.from({ length: count }, (_, i) => minObj.value + i);
      return (
        <div className={styles.numberRangeBoxContainer}>
          {(minLabel || maxLabel) && (
            <div className={styles.numberRangeLabels}>
              <span style={minLabel ? { color: colorMap[minColor], fontWeight: 600 } : undefined}>{minLabel}</span>
              <span style={maxLabel ? { color: colorMap[maxColor], fontWeight: 600 } : undefined}>{maxLabel}</span>
            </div>
          )}
          <div className={styles.numberRangeBoxes}>
            {boxes.map(n => (
              <button
                key={n}
                className={`${styles.numberRangeBox} ${sliderValue === n ? styles.numberRangeBoxSelected : ""}`}
                onClick={() => setSliderValue(n)}
              >
                {n}
              </button>
            ))}
          </div>
        </div>
      );
    }
    case "stars": {
      const stars = Array.from({ length: STAR_COUNT }, (_, i) => i + 1);
      return (
        <div className={styles.stars}>
          {stars.map(n => (
            <button
              key={n}
              type="button"
              aria-label={`${n} / ${STAR_COUNT}`}
              className={styles.star}
              onClick={() => setSliderValue(n)}
            >
              {sliderValue !== null && n <= sliderValue ? "★" : "☆"}
            </button>
          ))}
        </div>
      );
    }
    case "integer": {
      const { min, max } = responseType;
      return (
        <div className={styles.integerContainer}>
          <TextField
            type="number"
            variant="outlined"
            size="small"
            inputProps={{ min, max }}
            placeholder={`${min} - ${max}`}
            fullWidth
            value={integerValue}
            onChange={e => setIntegerValue(e.target.value)}
          />
        </div>
      );
    }
    case "date":
      return (
        <div className={styles.dateContainer}>
          <TextField
            type="date"
            variant="outlined"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={dateValue}
            onChange={e => setDateValue(e.target.value)}
          />
        </div>
      );
    case "text": {
      const multiline = responseType.multiline;
      return (
        <div className={styles.textContainer}>
          <TextField
            variant="outlined"
            size={multiline ? undefined : "small"}
            multiline={multiline}
            rows={multiline ? 4 : undefined}
            fullWidth
            placeholder="Enter your answer..."
            value={textValue}
            onChange={e => setTextValue(e.target.value)}
          />
        </div>
      );
    }
    case "text-area":
      return (
        <div className={styles.textContainer}>
          <TextField
            variant="outlined"
            multiline
            rows={4}
            fullWidth
            placeholder="Enter your answer..."
            value={textValue}
            onChange={e => setTextValue(e.target.value)}
          />
        </div>
      );
    default:
      return null;
  }
};

const isRequirementSatisfied = (requirement: Requirement | undefined, feelCtx: Record<string, unknown>): boolean =>
  requirement === "required" ||
  (typeof requirement === "object" && evaluateFeelPredicate(requirement.requiredWhen, feelCtx));

export const RevealedQuestions: React.VFC<{ questions: ResolvedQuestionRef[] }> = ({ questions }) => {
  const feelContext = React.useContext(FeelContext);
  const respondedIds = React.useContext(RespondedContext);

  const revealedIds = React.useMemo(() => {
    const refs = questions
      .filter(q => q.question)
      .map(q => ({ questionId: q.question.id, visibility: q.visibility, requirement: q.requirement }));
    return computeRevealedQuestions(refs, feelContext, respondedIds);
  }, [questions, feelContext, respondedIds]);

  return (
    <>
      {questions.map(qRef => {
        if (!qRef.question || !revealedIds.has(qRef.question.id)) return null;
        return (
          <ResolvedQuestionPreview
            key={qRef.question.id}
            question={qRef.question}
            isRequired={isRequirementSatisfied(qRef.requirement, feelContext)}
          />
        );
      })}
    </>
  );
};

export const ResolvedQuestionPreview: React.VFC<{
  question: ResolvedQuestion;
  isRequired?: boolean;
}> = ({ question, isRequired }) => {
  const { registerRequired, unregisterRequired, errorIds } = React.useContext(ValidationContext);
  const pageIndex = React.useContext(PageContext);
  const questionId = question.id;

  React.useEffect(() => {
    if (!isRequired || questionId == null) return;
    registerRequired(questionId, pageIndex);
    return () => unregisterRequired(questionId);
  }, [isRequired, questionId, pageIndex, registerRequired, unregisterRequired]);

  const hasError = questionId != null && errorIds.has(questionId);

  return (
    <Box className={`${styles.questionInGroup} ${hasError ? styles.questionError : ""}`}>
      <Box className={styles.questionHeader}>
        <Typography variant="subtitle1" className={styles.questionTitle}>
          {question.question}
        </Typography>
      </Box>
      {question.description && (
        <Typography variant="body2" className={styles.questionDescription}>
          <LinkifiedText text={question.description} />
        </Typography>
      )}
      {question.response_type && (
        <ResolvedResponseTypePreview
          responseType={question.response_type}
          questionId={question.id}
          feelId={question.feel_id}
        />
      )}
      {hasError && (
        <Typography variant="caption" className={styles.errorText}>
          This question is required
        </Typography>
      )}
    </Box>
  );
};
